import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  ADDRESS_STEP,
  CHECKOUT_STEP_MAP,
  EDIT_ADDRESS_STEP,
  NEW_ADDRESS_STEP,
  REVIEW_STEP,
  SAVED_ADDRESS_STEP
} from 'constants/checkoutFlow';
import { ADDRESS_FIELDS } from 'constants/formFields';

import { trackEvent } from 'helpers/analytics';
import {
  addNewAddress,
  clearAddressErrors,
  clearSelectedAddress,
  clearSuggestedAddresses,
  deleteCheckoutAddress,
  fetchAddresses,
  newAddressPrimary,
  receiveNewShippingField,
  setAddressesAreLoadingMessage,
  setSelectedSuggestedAddress,
  setShowMaybePoBoxHelperText,
  storeSelectedAddress
} from 'actions/checkout/addresses';
import {
  advanceCheckout,
  fetchCheckoutData,
  setIsAlsoBilling,
  trackHasSeenIsAlsoBilling,
  updateCheckoutForTargetStep
} from 'actions/checkout/purchase';
import { PO_BOX_RE } from 'common/regex';
import AddressList from 'components/checkout/caap/address/addresslist/AddressList';
import SectionLoading from 'components/checkout/caap/SectionLoading';
import ContentBox from 'components/checkout/caap/ContentBox';
import DigitalDeliveryOnlyAddress from 'components/checkout/caap/addressformats/DigitalDeliveryOnlyAddress';
import ErrorAlert from 'components/checkout/caap/ErrorAlert';
import NewAddressModal from 'components/checkout/caap/address/NewAddressModal';
import ShippingAddress from 'components/checkout/caap/address/ShippingAddress';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import StepSection from 'components/checkout/caap/StepSection';
import SuggestedAddresses from 'components/checkout/caap/address/SuggestedAddresses';
import { getPathFromStep } from 'helpers/CheckoutFlowControl';
import {
  isChangingShipping,
  isDigitalDeliveryOnlyCart,
  isGeneralAddressContstraintPresent,
  isInactiveShippingAddress,
  isMissingShippingDestination,
  isShippableAddress,
  needsValidShippingAddress
} from 'helpers/CheckoutUtils';
import marketplace from 'cfg/marketplace.json';
import { isDesktop } from 'helpers/ClientUtils';
const { ADDRESS_LINE_1, ADDRESS_LINE_2 } = ADDRESS_FIELDS;

export class ShipSection extends Component {
  componentDidMount() {
    const {
      location: { search },
      checkoutData: {
        addresses: { new: newAddress, saved: savedAddresses },
        purchaseData: {
          targetStep
        }
      }
    } = this.props;

    const { editDestinationId } = queryString.parse(search);
    const isAddressStep = targetStep === ADDRESS_STEP;
    const isNewAddressStep = targetStep === NEW_ADDRESS_STEP;
    const isEditAddressStep = targetStep === EDIT_ADDRESS_STEP;
    const hasAddressId = !!(newAddress.addressId && newAddress.addressId.length);
    const hasSavedAddresses = !!(savedAddresses && savedAddresses.length);

    if (isAddressStep) {
      if (hasSavedAddresses) {
        // do nothing, could default the address here
        // this.props.defaultSelectedPayment();
      } else if (hasAddressId) {
        this.props.clearSelectedAddress();
      }
    } else if (isNewAddressStep && hasAddressId) {
      this.props.clearSelectedAddress();
    } else if (isEditAddressStep && !hasAddressId) {
      this.props.storeSelectedAddress(editDestinationId);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      checkoutData: {
        addresses: { invalidFields, saved: savedAddresses },
        paymentTypes: { saved: savedPayments },
        purchaseData: {
          targetStep,
          hasSeenIsAlsoBilling
        }
      },
      trackHasSeenIsAlsoBilling,
      clearSelectedAddress,
      clearAddressErrors

    } = this.props;

    const {
      checkoutData: {
        purchaseData: {
          targetStep: prevTargetStep
        }
      }
    } = prevProps;

    const isAddressStepAsNew = targetStep === ADDRESS_STEP && !savedAddresses.length;
    const isNewAddressStep = targetStep === NEW_ADDRESS_STEP || isAddressStepAsNew;
    const isEditAddressStep = targetStep === EDIT_ADDRESS_STEP;
    const wasPrevStepAddressStepWithInvalidFields = prevTargetStep === ADDRESS_STEP && invalidFields.length;
    const wasPrevStepNewAddressStep = prevTargetStep === NEW_ADDRESS_STEP || wasPrevStepAddressStepWithInvalidFields;
    const shouldShowUseAsBillingAddress = !(savedAddresses.length && savedPayments.length);

    if (isNewAddressStep && shouldShowUseAsBillingAddress && !hasSeenIsAlsoBilling) {
      // if user has actually hit the new address page, been presented with the checkbox,
      // and hasn't yet been identified as having seen the checkbox, then track that it
      // has been seen
      trackHasSeenIsAlsoBilling();
    }

    // when user jumps to the 'add new' screen, make sure that address id is cleared
    // out in the event there was 1 there
    if (prevTargetStep !== targetStep && isNewAddressStep) {
      // was new address step, clearing errors!
      clearSelectedAddress();
      clearAddressErrors();
    }

    // when user jumps to 'edit' screen, make sure any pre-existing errors are cleared out
    // since an invalid address when adding a new address jumps to 'edit', ignore clearing
    // errors in that case
    if (prevTargetStep !== targetStep && isEditAddressStep && !wasPrevStepNewAddressStep) {
      clearAddressErrors();
    }
  }

  onChangeShipAddressClick = () => {
    const {
      fetchAddresses,
      receiveNewShippingField
    } = this.props;

    trackEvent('TE_CHECKOUT_SPC_CHANGE_SHIPPING');
    fetchAddresses();
    receiveNewShippingField('isBilling', false);
  }

  onCloseLinkClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_SEL_ADDRESS');
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  onCloseNewAddressModalOverlayClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_ADDRESS_MODAL', 'shipping');
    updateCheckoutForTargetStep({ targetStep: ADDRESS_STEP, newUrl: getPathFromStep(ADDRESS_STEP) });
  }

  onCloseSuggestedAddressModalOverlayClick = e => {
    e.preventDefault();
    trackEvent('TE_CHECKOUT_SPC_CLOSE_SUGG_ADDRESS_MODAL');
    this.onUseThisSuggestedAddressClick(e);
  }

  onCountryDropdownChange = e => {
    const { receiveNewShippingField } = this.props;
    const { name, value } = e.currentTarget;
    receiveNewShippingField(name, value);
  }

  onDeleteAddressClick = addressId => {
    const {
      deleteCheckoutAddress
    } = this.props;

    trackEvent('TE_CHECKOUT_AL_SELECT_FOR_DELETE');
    deleteCheckoutAddress({ addressId });
  }

  onEditAddressClick = selectedAddressId => {
    const {
      checkoutData,
      receiveNewShippingField
    } = this.props;

    const { addresses: { saved: savedAddresses } } = checkoutData;

    const address = (savedAddresses || []).find(
      item => item.addressId === selectedAddressId);

    const {
      addressId,
      name,
      phone,
      mailingAddress: {
        addressLine1,
        addressLine2,
        city,
        countryCode,
        stateOrRegion,
        postalCode
      }
    } = address;

    trackEvent('TE_CHECKOUT_AL_SELECT_FOR_EDIT');
    receiveNewShippingField('addressId', addressId);
    receiveNewShippingField('fullName', name ? name.fullName : '');
    receiveNewShippingField('primaryVoiceNumber', phone ? phone.voice.primary.number : '');
    receiveNewShippingField('addressLine1', addressLine1);
    receiveNewShippingField('addressLine2', addressLine2 ? addressLine2 : '');
    receiveNewShippingField('city', city);
    receiveNewShippingField('countryCode', countryCode);
    receiveNewShippingField('stateOrRegion', stateOrRegion);
    receiveNewShippingField('postalCode', postalCode);
    this.goToEditAddress(addressId);
  }

  onShipAddressSelected = addressId => {
    const { receiveNewShippingField } = this.props;
    trackEvent('TE_CHECKOUT_AL_SELECT_ADDRESS');
    receiveNewShippingField('addressId', addressId);
  }

  onShipToThisAddressClick = e => {
    e.preventDefault();
    const {
      addNewAddress,
      checkoutData: {
        addresses: { saved: savedAddresses },
        purchaseData: { isFetchingCheckoutData }
      },
      newAddressPrimary } = this.props;

    const primaryAddress = (savedAddresses || []).find(
      item => item.isDefaultShippingAddress);
    const hasPrimaryAddress = !!primaryAddress;

    trackEvent('TE_CHECKOUT_AM_SHIP_TO_ADDRESS');

    if (!isFetchingCheckoutData) {
      // when adding a new address, if user has no primary address already, make the new address primary
      if (!hasPrimaryAddress) {
        newAddressPrimary(true);
      }
      addNewAddress();
    }
  }

  onShippingAddressFieldChange = e => {
    const { receiveNewShippingField, setShowMaybePoBoxHelperText } = this.props;
    const { name, value } = e.currentTarget;
    const isAddressLineField = name === ADDRESS_LINE_1.fieldName || name === ADDRESS_LINE_2.fieldName;
    const maybePoBox = value.match(PO_BOX_RE);
    setShowMaybePoBoxHelperText(isAddressLineField && !!maybePoBox);
    receiveNewShippingField(name, value);
  }

  onSuggestedAddressSelected = addressId => {
    const { setSelectedSuggestedAddress } = this.props;
    trackEvent('TE_CHECKOUT_SA_SELECT_ADDRESS');
    setSelectedSuggestedAddress(addressId);
  }

  onToggleIsAlsoBillingClick = () => {
    const { checkoutData:  { purchaseData: { isAlsoBilling } }, setIsAlsoBilling } = this.props;
    const targetValue = !isAlsoBilling;
    trackEvent('TE_CHECKOUT_AM_TOGGLE_IS_BILLING', `${targetValue}`);
    setIsAlsoBilling(targetValue);
  }

  onToggleIsUseAsDefaultAddressClick = () => {
    const { checkoutData:  { addresses: { isNewPrimary } }, newAddressPrimary } = this.props;
    const targetValue = !isNewPrimary;
    trackEvent('TE_CHECKOUT_AM_TOGGLE_IS_DEFAULT', `${targetValue}`);
    newAddressPrimary(targetValue);
  }

  onUseThisShippingClick = addressId => {
    const {
      advanceCheckout,
      checkoutData: {
        addresses: { saved: savedAddresses },
        purchaseData: { data: { shippingAddressId }, isFetchingCheckoutData }
      },
      fetchCheckoutData,
      receiveNewShippingField,
      setAddressesAreLoadingMessage
    } = this.props;

    // track which was clicked from positional point of view
    const index = savedAddresses.findIndex(item => item.addressId === addressId);

    trackEvent('TE_CHECKOUT_AL_SHIP_TO_ADDRESS', `${index}`);
    receiveNewShippingField('addressId', addressId);

    if (!isFetchingCheckoutData) {
      if (addressId !== shippingAddressId) {
        setAddressesAreLoadingMessage('Setting your shipping address...');
        fetchCheckoutData({ advanceOnSuccess: true, addressId, showFullPageSpinner: false });
      } else {
        advanceCheckout();
      }
    }
  }

  onUseThisSuggestedAddressClick = e => {
    e.preventDefault();
    const {
      addNewAddress,
      advanceCheckout,
      checkoutData: {
        addresses: {
          suggestedAddresses: { addresses, isBilling, selectedAddressId }
        },
        purchaseData: {
          data: { shippingAddress },
          purchaseCreditCard
        }
      },
      clearSuggestedAddresses,
      receiveNewShippingField
    } = this.props;

    const suggestedAddressMatch = selectedAddressId.match(/(.*)-(\d+)$/);

    let { fullName, phoneNumber } = shippingAddress;

    if (isBilling) {
      ({ fullName } = purchaseCreditCard.billingAddress);
      ({ phoneNumber } = purchaseCreditCard.billingAddress);
    }

    if (suggestedAddressMatch) {
      const {
        mailingAddress: {
          addressLine1,
          addressLine2,
          city,
          countryCode,
          postalCode,
          stateOrRegion
        }
      } = addresses[suggestedAddressMatch[2]];

      trackEvent('TE_CHECKOUT_SA_USE_ADDRESS', 'suggested');

      receiveNewShippingField('isBilling', isBilling);
      receiveNewShippingField('addressId', suggestedAddressMatch[1]);
      receiveNewShippingField('fullName', fullName);
      receiveNewShippingField('primaryVoiceNumber', phoneNumber);
      receiveNewShippingField('addressLine1', addressLine1);
      receiveNewShippingField('addressLine2', addressLine2 ? addressLine2 : '');
      receiveNewShippingField('city', city);
      receiveNewShippingField('countryCode', countryCode);
      receiveNewShippingField('stateOrRegion', stateOrRegion);
      receiveNewShippingField('postalCode', postalCode);
      addNewAddress();
    } else {
      trackEvent('TE_CHECKOUT_SA_USE_ADDRESS', 'original');
      clearSuggestedAddresses();
      advanceCheckout();
    }
  }

  goToEditAddress = addressId => {
    const { updateCheckoutForTargetStep } = this.props;
    const newUrl = getPathFromStep(EDIT_ADDRESS_STEP);
    updateCheckoutForTargetStep({ targetStep: EDIT_ADDRESS_STEP, newUrl, suppliedQs: `editDestinationId=${addressId}` });
  }

  showShippingAddressStep = () => {
    const { targetStep } = this.props.checkoutData.purchaseData;
    const { shipOptions } = this.props.checkoutData;
    return targetStep === ADDRESS_STEP
    || targetStep === NEW_ADDRESS_STEP
    || targetStep === SAVED_ADDRESS_STEP
    || targetStep === EDIT_ADDRESS_STEP
    || (targetStep === REVIEW_STEP && !shipOptions.showModal);
  }

  render() {
    const {
      addresses,
      paymentTypes: { saved: savedPayments },
      purchaseData: {
        cartType,
        constraintViolations = {},
        data: { purchaseId, shippingAddress, shippingAddressId },
        isAlsoBilling,
        isFetchingCheckoutData,
        isNewCustomer,
        pageHistory,
        targetStep
      }
    } = this.props.checkoutData;

    const {
      addressAlertMsg: addressMessage,
      invalidFields,
      loadingMessage,
      new: newAddress,
      saved: savedAddresses,
      suggestedAddresses,
      deletedLastAddress,
      showPoBoxHelperText
    } = addresses;

    const {
      showSuggestedModal
    } = suggestedAddresses;

    const isAddressStep = targetStep === ADDRESS_STEP;
    const isNewAddressStep = targetStep === NEW_ADDRESS_STEP;
    const isEditAddressStep = targetStep === EDIT_ADDRESS_STEP;
    const isSavedAddressStep = targetStep === SAVED_ADDRESS_STEP;
    /*
     if we are on the address step, determine which address modal to show,
     where as if we are specifically on the new or saved address step, show
     the corresponding modal

     if we have no addresses, but we do have a message from deleting the last item
     that we did have, then we want to show the saved address list (it'll be empty),
     but thats ok, as the message will be shown and they can add from there
    */

    let showNewAddress = false, showSavedAddresses = false;
    if (isAddressStep) {
      if (savedAddresses && savedAddresses.length || deletedLastAddress) {
        showSavedAddresses = true;
      } else {
        showNewAddress = true;
      }
    } else if (isNewAddressStep || isEditAddressStep) {
      showNewAddress = true;
    } else if (isSavedAddressStep) {
      showSavedAddresses = true;
    }

    const isDigitalDeliveryOnly = isDigitalDeliveryOnlyCart(cartType);
    const selectedAddressId = (newAddress && newAddress.addressId) || shippingAddressId || null;
    const showShippingAddressStep = this.showShippingAddressStep();
    const showAsInline = !!isNewCustomer;
    const { testId = f => f } = this.context;
    const isChangeShipping = isChangingShipping(targetStep);
    const showSuggestedAddresses = showSuggestedModal && isChangeShipping;
    const shouldShowUseAsBillingAddress = !(savedAddresses.length && savedPayments.length);
    const shouldShowIsNewPrimary = !shouldShowUseAsBillingAddress && isNewAddressStep;
    const hasInvalidAddress = !!(invalidFields && invalidFields.length);
    const errorMessageText = `Your order cannot be shipped to the current address.  Please ${savedAddresses.length > 1 ? 'select' : 'add'} a new one.`;
    const isMissingDestination = isMissingShippingDestination(constraintViolations);
    const hasGeneralAddressConstraint = isGeneralAddressContstraintPresent(constraintViolations);
    const needsValidShipping = needsValidShippingAddress(constraintViolations);
    const isAddressShippable = shippingAddress && shippingAddress.countryCode && isShippableAddress(shippingAddress.countryCode);
    const hasAddressAndItIsNotShippable = shippingAddress && !isAddressShippable;
    const hasInactiveShippingAddress = isInactiveShippingAddress(constraintViolations);
    const hasInactiveNotFromRecentDelete = hasInactiveShippingAddress && !addressMessage;

    // show error message when:
    //  - shipping address has not been deactivated (aka, just deleted)
    //  - shipping address present but is not shippable
    //  - purchase has shipping addresses available and needs valid shipping (excluding general address CV and destination missing CV)
    const errorMessage = (hasInactiveNotFromRecentDelete || hasAddressAndItIsNotShippable || savedAddresses.length && !hasGeneralAddressConstraint && !isMissingDestination && needsValidShipping)
      ? <ErrorAlert message={errorMessageText} />
      : null;

    if (showSuggestedAddresses) {
      return (
        <StepSection
          showTitleBar={showAsInline}
          title="Choose a shipping address"
          stepNumber="1"
          addOverlay={isFetchingCheckoutData}
          isCurrentStep={showShippingAddressStep}>
          <SuggestedAddresses
            address={shippingAddress}
            isFetchingCheckoutData={isFetchingCheckoutData}
            onCloseModalOverlayClick={this.onCloseSuggestedAddressModalOverlayClick}
            onSuggestedAddressSelected={this.onSuggestedAddressSelected}
            onUseThisAddressClick={this.onUseThisSuggestedAddressClick}
            selectedAddressId={suggestedAddresses.selectedAddressId}
            showAsModal={!showAsInline}
            suggestedAddresses={suggestedAddresses.addresses}
            dataTestIdLoader={`${testId('spinner')}`} />
        </StepSection>
      );
    }

    if (isDesktop(marketplace) && loadingMessage && loadingMessage.length) {
      return (
        <StepSection
          showTitleBar={false}
          title="Choose a shipping address"
          stepNumber="1"
          addOverlay={false}
          isCurrentStep={true}
          dataTestIdLoader={`${testId('spinner')}`}>
          <div>
            <SmallScreenOnly><h2>Shipping address</h2></SmallScreenOnly>
            <ContentBox
              isSolo={true}
              isNonRadio={true}>
              <SectionLoading
                heading="Shipping address"
                msg={loadingMessage}
                step="1" />
            </ContentBox>
          </div>
        </StepSection>
      );
    }

    return (
      <StepSection
        addOverlay={false}
        linkTo={`${CHECKOUT_STEP_MAP[REVIEW_STEP]}?referrer=spc&pid=${purchaseId}`}
        linkText="Close"
        isCurrentStep={showShippingAddressStep}
        onLinkClick={this.onCloseLinkClick}
        showLink={isChangeShipping && shippingAddressId}
        showTitleBar={isChangeShipping}
        stepNumber="1"
        title="Choose a shipping address"
        dataTestIdLoader={`${testId('spinner')}`}>
        {
          showSavedAddresses || showNewAddress
            ? null
            : isDigitalDeliveryOnly
              ? <div data-test-id={testId('digitalDeliveryOnly')}>
                <SmallScreenOnly><h2>Digital Delivery:</h2></SmallScreenOnly>
                <ContentBox
                  isSolo={true}
                  isNonRadio={true}>
                  <DigitalDeliveryOnlyAddress />
                </ContentBox>
              </div>
              : <div data-test-id={testId('mixedDelivery')}>
                <SmallScreenOnly><h2>Shipping Address</h2></SmallScreenOnly>
                <ContentBox
                  isSolo={true}
                  isNonRadio={true}>
                  <ShippingAddress
                    address={shippingAddress}
                    onChangeAddressClick={this.onChangeShipAddressClick}
                    purchaseId={purchaseId} />
                </ContentBox>
              </div>
        }

        {
          showSavedAddresses
            ? <AddressList
              addresses={savedAddresses}
              addressMessage={addressMessage}
              errorMessage={errorMessage}
              isFetchingCheckoutData={isFetchingCheckoutData}
              onAddressSelected={this.onShipAddressSelected}
              onDeleteAddressClick={this.onDeleteAddressClick}
              onEditAddressClick={this.onEditAddressClick}
              pageHistory={pageHistory}
              purchaseId={purchaseId}
              onUseThisAddressClick={this.onUseThisShippingClick}
              selectedAddressId={selectedAddressId}
              shippingAddressId={shippingAddressId} />
            : null
        }

        { showNewAddress
          ? <NewAddressModal
            address={addresses.new}
            hasInvalidAddress={hasInvalidAddress}
            isNewPrimary={addresses.isNewPrimary}
            isAlsoBilling={isAlsoBilling}
            isEditAddressStep={isEditAddressStep}
            isFetchingCheckoutData={isFetchingCheckoutData}
            onAddressFieldChange={this.onShippingAddressFieldChange}
            onCloseModalOverlayClick={this.onCloseNewAddressModalOverlayClick}
            onCountryDropdownChange={this.onCountryDropdownChange}
            onDeleteAddressClick={this.onDeleteAddressClick}
            onShipToThisAddressClick={this.onShipToThisAddressClick}
            onToggleIsAlsoBillingClick={this.onToggleIsAlsoBillingClick}
            onToggleIsUseAsDefaultAddressClick={this.onToggleIsUseAsDefaultAddressClick}
            pageHistory={pageHistory}
            shouldShowUseAsBillingAddress={shouldShowUseAsBillingAddress}
            shouldShowIsNewPrimary={shouldShowIsNewPrimary}
            showAsModal={!showAsInline}
            showPoBoxHelperText={showPoBoxHelperText}
          />
          : null
        }
      </StepSection>
    );
  }
}

function mapStateToProps(state) {
  return {
    checkoutData: state.checkoutData,
    location: state.routing.locationBeforeTransitions
  };
}

ShipSection.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  addNewAddress,
  advanceCheckout,
  clearSuggestedAddresses,
  clearAddressErrors,
  deleteCheckoutAddress,
  fetchAddresses,
  fetchCheckoutData,
  newAddressPrimary,
  receiveNewShippingField,
  setIsAlsoBilling,
  setSelectedSuggestedAddress,
  updateCheckoutForTargetStep,
  clearSelectedAddress,
  setAddressesAreLoadingMessage,
  storeSelectedAddress,
  trackHasSeenIsAlsoBilling,
  setShowMaybePoBoxHelperText
})(ShipSection);
